import { getFirstQueryParam } from '@jusbrasil-web/shared-utils-url'
import { type NextApiRequest, type NextApiResponse } from 'next'

import {
  AUTH_API_TIMEOUT,
  BIFROST_API,
  DEFAULT_ERROR_MESSAGE,
  PRODUCTION_ORIGIN,
} from '../constants'

export async function signupCpfProxyApiHandler(
  req: NextApiRequest,
  res: NextApiResponse
): Promise<void> {
  if (req.method !== 'POST') {
    res.setHeader('Allow', 'POST')
    res.status(405).end()
    return
  }

  const path = req.query.path
  const signupToken = Array.isArray(path) && path.length > 0 ? String(path[0]) : undefined
  const basePath = BIFROST_API.SIGNUP.CPF_BASED
  const upstreamPath = signupToken ? `${basePath}/${signupToken}` : basePath
  const upstreamUrl = `${PRODUCTION_ORIGIN}${upstreamPath}`

  const headerValue = (v: string | string[] | undefined): string => {
    if (v === undefined || v === '') return ''
    if (Array.isArray(v)) return v.join('; ')
    return String(v)
  }

  const cookie = headerValue(req.headers.cookie)
  const referer = headerValue(req.headers.referer)
  const origin = headerValue(req.headers.origin)
  const csrf = getFirstQueryParam(req.headers['csrf-token'] ?? req.headers['CSRF-Token'])

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(cookie && { Cookie: cookie }),
    ...(referer && { Referer: referer }),
    ...(origin && { Origin: origin }),
    ...(csrf && { 'CSRF-Token': csrf }),
  }

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), AUTH_API_TIMEOUT)

  try {
    const upstream = await fetch(upstreamUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(req.body ?? {}),
      redirect: 'manual',
      signal: controller.signal,
    })
    clearTimeout(timeout)

    const status = upstream.status
    const isRedirect = status >= 300 && status < 400
    const location = upstream.headers.get('Location')

    let setCookies: string[] = []
    if (typeof upstream.headers.getSetCookie === 'function') {
      setCookies = upstream.headers.getSetCookie()
    } else if (upstream.headers.get('set-cookie')) {
      setCookies = [upstream.headers.get('set-cookie') as string]
    }

    if (setCookies.length > 0) {
      res.setHeader('Set-Cookie', setCookies)
    }

    if (isRedirect && location) {
      res.status(200).json({ mustRedirectTo: location })
      return
    }

    const contentType = upstream.headers.get('Content-Type') ?? ''
    const body = contentType.includes('application/json')
      ? await upstream.json()
      : await upstream.text()

    res.status(status)
    if (typeof body === 'string') {
      res.setHeader('Content-Type', contentType)
      res.send(body)
    } else {
      res.json(body)
    }
  } catch (err) {
    clearTimeout(timeout)
    console.error(err)
    res.status(500).json({ message: DEFAULT_ERROR_MESSAGE })
  }
}
